import React from 'react';

import CalloutBadge from '@rover/components/src/callouts/CalloutBadge';
import { Box, Flex, Text } from '@rover/kibble/core';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';

import LegacyPriceColumn from '../../SearchResultCard/PriceColumn/LegacyPriceColumn';

import AvatarWithBadge from './AvatarWithBadge';

export type Props = {
  searchResult: OptimizedSearchResult;
  language: string;
  priceTransparencyLevel?: PriceTransparencyLevel;
};

const ProfileStatusBadge: React.FC<OptimizedSearchResult['profileStatusBadgeData']> = ({
  slug,
  text,
}) => {
  return (
    <Box>
      {slug === 'star-sitter' && (
        <CalloutBadge
          value={text}
          calloutBadgeStyle={CalloutBadge.Styles.STAR_SITTER}
          itemProp="award"
        />
      )}
      {slug === 'daycare-center' && (
        <CalloutBadge
          value={text}
          calloutBadgeStyle={CalloutBadge.Styles.LOCAL_FACILITY}
          itemProp="award"
        />
      )}
    </Box>
  );
};

const ProfileNameAndPriceRow: React.FC<Props> = ({
  searchResult,
  language,
  priceTransparencyLevel,
}: Props) => {
  const {
    defaultImage,
    currencyCode,
    price,
    shortName,
    profileStatusBadgeData,
    unitAsSentence,
    primaryBadgeData,
    isFacility,
  } = searchResult;

  return (
    <Flex gap="4x" flexDirection="row" justifyContent="space-between" alignItems="center">
      <Flex gap="4x" flexDirection="row" alignItems="center">
        {/* Profile Avatar */}
        <AvatarWithBadge
          imageUrl={defaultImage}
          badge={primaryBadgeData}
          shortName={shortName}
          isFacility={isFacility}
        />
        <Flex gap="1x" flexDirection="column">
          {/* Sitter Name */}
          <Text itemProp="name" textColor="primary" size="200" fontWeight="semibold">
            {shortName}
          </Text>
          {/* 💭 Note: The CarouselSitterCard does not display all the same badges as SearchResultsCard. This is intentional but may be revisited in the future */}
          {profileStatusBadgeData && <ProfileStatusBadge {...profileStatusBadgeData} />}
        </Flex>
      </Flex>

      {/* Price */}
      <LegacyPriceColumn
        currencyCode={currencyCode}
        language={language}
        price={price}
        unitAsSentence={unitAsSentence}
        priceTransparencyLevel={priceTransparencyLevel}
      />
    </Flex>
  );
};

export default ProfileNameAndPriceRow;
